import React from 'react';
import { motion } from 'motion/react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const faqs = [
  {
    question: "What age groups is Goodikids suitable for?",
    answer: "Goodikids is designed for children and teens aged 5 to 18 years, with courses thoughtfully structured according to every stage of growth. For ages 5–8, children build early foundations in behaviour, social skills, values, confidence, and self-discipline. For ages 9–12, learners develop emotional understanding, problem-solving, teamwork, and practical life skills. For ages 13–15, teenagers improve communication, self-control, decision-making, and leadership qualities. For ages 16–18, young adults are guided toward career mindset, stress management, real-life responsibility, and future readiness. Each age group receives content that is relatable, practical, and developmentally suitable—helping them grow step by step into confident, kind, and responsible individuals."
  },
  {
    question: "Is Goodikids really free?",
    answer: "Yes! Goodikids offers a free plan where a few topics in every course are unlocked for all users. You also get daily free MCQs, daily learning through flashcards and banners, and basic progress tracking at no cost. However, to unlock all topics of a particular course, unlimited MCQs, full certificates, detailed reports, and complete learning access, you will need to purchase a subscription plan."
  },
  {
    question: "What subjects does Goodikids cover?",
    answer: "Goodikids does not teach academic subjects like maths or science. Instead, it focuses on character development and real-life skills that help children and teens grow into confident and responsible individuals. Our courses include values and behaviour, emotional intelligence, confidence building, discipline, communication skills, manners and etiquette, safety awareness, leadership qualities, social skills, problem-solving, decision-making, time management, study skills, stress management, moral values, and overall personality development. Every topic is practical, relatable, and age-appropriate for learners aged 5 to 18 years."
  },
  {
    question: "Can I track my child's progress?",
    answer: "Absolutely! Both free and premium plans include progress tracking. Premium plans offer advanced progress tracking with detailed insights, parent dashboards, and performance analytics."
  },
  {
    question: "Is the app safe for kids?",
    answer: "Yes, absolutely. Safety is one of the highest priorities at Goodikids. The app is 100% kid-friendly and ad-free, with no external links, no inappropriate content, and no social media exposure. Every course, image, video, and activity is designed to be emotionally safe and age-appropriate for children and teens. All user data and learning progress are stored securely, so children can learn in a private and protected environment. Parents can confidently allow their children to use the app without worrying about distractions or unsafe content."
  },
  {
    question: "What devices does Goodikids support?",
    answer: "Goodikids is available on iOS (iPhone and iPad) and Android devices. Premium subscribers also get offline mode for learning on the go."
  },
  {
    question: "Can I cancel my subscription anytime?",
    answer: "Yes, you can cancel your subscription at any time. If you cancel, you'll continue to have access to premium features until the end of your billing period."
  },
  {
    question: "Does Goodikids offer certificates?",
    answer: "Yes! Kids earn certificates for completing milestones and achieving learning goals. Free plan users get basic certificates, while premium users receive beautifully designed premium certificates."
  },
  {
    question: "How many kids can use one account?",
    answer: "Each Goodikids subscription is designed for one child only so that learning, progress reports, analytics, and certificates remain accurate and personalised. A single account cannot be shared among multiple children. If you have more than one child, each child will need their own separate subscription to access their personalised courses and progress tracking."
  },
  {
    question: "Do you offer a free trial?",
    answer: "You can start with our free plan immediately - no credit card required! This gives you a great taste of what Goodikids offers before upgrading to premium."
  }
];

const FAQ = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Questions</span>
            </h1>
            <p className="text-xl text-orange-600 max-w-3xl mx-auto">
              Find answers to common questions about Goodikids
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-2 border-gray-200 rounded-2xl px-6">
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-orange-500 py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 mb-4">Still have questions?</h3>
          <p className="text-xl text-orange-600 mb-8">Our support team is here to help!</p>
          <a href="mailto:support@goodikids.com" className="inline-block bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white rounded-full px-8 py-4 font-semibold transition-all">
            Contact Support
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;